import React, { useEffect, useState } from 'react';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography, TextField, Grid, CircularProgress, Paper, Autocomplete, Chip } from "@mui/material";
import RoutingSettingsDialog, { resumenParticular } from "app/pages/trips/RoutingSettingsDialog";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { LoadingButton } from '@mui/lab';
import vehicleServices from 'app/services/vehicle-services';

const VehicleEditModal = props => {
    const {recordID, isOpen, onClose=()=>{}, onRecordChange=()=>{}, onRecordAdd=()=>{}} = props;
    const { authUser } = useJumboAuth();
    const [currentRecord, setCurrentRecord] = useState(null)
    const [changed, setChanged] = useState(false)
    const [editOptions, setEditOptions] = useState(null)
    const [loadingRecord, setLoadingRecord] = useState(false)
    const [saving, setSaving] = useState(false)
    const [routingOpen, setRoutingOpen] = useState(false);
    const [toggleActivationLoading, setToggleActivationLoading] = useState(false)

    useEffect(() => {
        if (authUser) {
          //   console.log(authUser.current_company);
        } else {
            console.log('authUser is not defined yet.');
        }
    }, [authUser]);

    useEffect(() => {
        if(recordID){
            setChanged(false)
            loadRecord(recordID)
        } else {
            vehicleServices.getNewVehicle('?include=transport_unit1,transport_unit2').then(resp => {
                setCurrentRecord({ ...resp, active: resp?.active ?? true })
            });
        }
        if(!editOptions){
            vehicleServices.getEditOptions().then(resp => { setEditOptions(resp) });
        }
    }, [recordID])

    const loadRecord = (id) => {
        if(!id) return
        setLoadingRecord(true)
        setCurrentRecord(null)
        vehicleServices.getVehicle(id, '?include=transport_unit1,transport_unit2')
            .then(resp => {
                setCurrentRecord(resp)
                setLoadingRecord(false)
            })
            .catch(error => {
                setLoadingRecord(false)
            });
    }

    const saveRecord = () => {
        setSaving(true)
        if(currentRecord.id){
            vehicleServices.saveVehicle(currentRecord.id, "?include=transport_unit1,transport_unit2", { vehicle: currentRecord })
                .then(resp => {
                    onRecordChange(resp)
                    onClose()
                    setSaving(false)
                })
                .catch(function (error) {
                    setSaving(false)
                });
        } else {
            vehicleServices.createVehicle("?include=transport_unit1,transport_unit2", { vehicle: currentRecord })
                .then(resp => {
                    onRecordAdd(resp)
                    onClose()
                    setSaving(false)
                })
                .catch(function (error) {
                    setSaving(false)
                });
        }
    }

    // El dialogo de ruteo guarda contra el servidor por su cuenta, asi que el resumen queda
    // viejo hasta releerlo. Se releen solo los campos de ruteo y no el registro completo: un
    // loadRecord aca borraria lo que el usuario tenga a medio editar en el formulario.
    const refreshRoutingSettings = () => {
        if(!currentRecord?.id) return

        vehicleServices.getVehicle(currentRecord.id, '?include=transport_unit1,transport_unit2')
            .then(resp => {
                setCurrentRecord(prev => ({
                    ...prev,
                    avoid_tolls: resp.avoid_tolls,
                    routing_settings: resp.routing_settings,
                    routing_settings_effective: resp.routing_settings_effective,
                    company_routing_settings: resp.company_routing_settings,
                }))
                onRecordChange(resp)
            })
            .catch(function (error) {});
    }

    const toggleActivation = () => {
        if(!currentRecord) return

        if(!currentRecord?.id){
            setCurrentRecord({ ...currentRecord, active: !currentRecord?.active })
            return
        }

        setToggleActivationLoading(true)
        vehicleServices.toggleVehicleActivation(currentRecord.id, '?include=transport_unit1,transport_unit2')
            .then(resp => {
                setCurrentRecord(resp)
                onRecordChange(resp)
            })
            .catch(function (error) {})
            .finally(() => {
                setToggleActivationLoading(false)
            });
    }

    const renderTransportHeaderFields = () => {
        return (
            <Grid container spacing={1} alignItems={"baseline"} sx={{pt: 1, mb: 3}}>
                <Grid item xs={6} lg={4}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Código"
                        size="small"
                        required
                        error={!currentRecord?.code}
                        value={currentRecord?.code || ''}
                        onChange={(e) => setCurrentRecord({...currentRecord, code: e.target.value})}
                    />
                </Grid>
                <Grid item xs={6} lg={4}>
                    <Autocomplete
                        size="small"
                        freeSolo
                        blurOnSelect
                        options={editOptions?.vehicle_kinds || []}
                        getOptionLabel={(option) => `${option[2]} - ${option[0]}`}
                        value={_.find(editOptions?.vehicle_kinds, ['1', currentRecord?.kind]) }
                        onChange={(e, v) => { if(_.get(v, '1')) { setCurrentRecord({ ...currentRecord, kind: v[1] }) } }}
                        renderInput={(params) => <TextField {...params} label="Tipo *" sx={{background: 'white'}} />}
                    />
                </Grid>
                <Grid item xs={6} lg={4}>
                    <Autocomplete
                        size="small"
                        freeSolo
                        blurOnSelect
                        options={editOptions?.centres || []}
                        getOptionLabel={(option) => option.code ? `${option.code} - ${option.name}` : `${option.name}`}
                        value={_.find(editOptions?.centres, ['id', currentRecord?.centre_id]) }
                        onChange={(e, v) => { setCurrentRecord({ ...currentRecord, centre_id: v?.id }) }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Bodega principal *"
                                helperText="Desde acá arranca la ruta del viaje"
                                sx={{background: 'white'}}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} lg={8}>
                    <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        options={(editOptions?.centres || []).filter((c) => c.id !== currentRecord?.centre_id)}
                        getOptionLabel={(option) => option.code ? `${option.code} - ${option.name}` : `${option.name}`}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        value={(editOptions?.centres || []).filter((c) => _.includes(currentRecord?.extra_centre_ids || [], c.id))}
                        onChange={(e, v) => { setCurrentRecord({ ...currentRecord, extra_centre_ids: _.map(v, 'id') }) }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Otras bodegas"
                                helperText="Otras bodegas desde las que este vehículo también carga"
                                sx={{background: 'white'}}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        )
    }
    
    const renderTransportUnitFields = (unit) => {
        return (

            <Paper variant="outlined" sx={{mt:1, p: 2}}>
                <Grid container spacing={1} alignItems={"baseline"}>
                    <Grid item xs={6} lg={4}>
                        <TextField
                            autoFocus
                            fullWidth
                            label={authUser?.current_company?.id === 80 ? "Nombre barcaza" : "Placa patente"}
                            size="small"
                            error={!_.get(currentRecord, `transport_unit${unit}.ppu`)}
                            value={_.get(currentRecord, `transport_unit${unit}.ppu`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}.ppu`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} lg={4}>
                        <Autocomplete
                            size="small"
                            freeSolo
                            blurOnSelect
                            options={editOptions?.transport_unit_kinds || []}
                            getOptionLabel={(option) => `${option[2]} - ${option[0]}`}
                            value={_.find(editOptions?.transport_unit_kinds, ['1', _.get(currentRecord, `transport_unit${unit}.kind`)]) }
                            onChange={(e, v) => {
                                if(_.get(v, '1')) {
                                    var newObj = _.set(currentRecord, `transport_unit${unit}.kind`, v[1]);
                                    setCurrentRecord({...newObj})
                                }
                            }}
                            renderInput={(params) => <TextField {...params} label="Tipo" sx={{background: 'white'}} />}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={1} alignItems={"baseline"} sx={{pt: 1}}>
                    <Grid item xs={6} lg={3}>
                        <TextField
                            autoFocus
                            fullWidth
                            label={authUser?.current_company?.id === 80 ? "Naviera" : "Marca"}
                            size="small"
                            value={_.get(currentRecord, `transport_unit${unit}.brand`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}.brand`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} lg={3}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Modelo"
                            size="small"
                            value={_.get(currentRecord, `transport_unit${unit}._model`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}._model`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} lg={3}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Versión"
                            size="small"
                            value={_.get(currentRecord, `transport_unit${unit}._version`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}._version`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} lg={3}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Año"
                            size="small"
                            value={_.get(currentRecord, `transport_unit${unit}.year`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}.year`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>

                    <Grid item xs={6} lg={3}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Número de ejes"
                            size="small"
                            value={_.get(currentRecord, `transport_unit${unit}.axes_number`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}.axes_number`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} lg={3}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Tara"
                            size="small"
                            value={_.get(currentRecord, `transport_unit${unit}.tare`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}.tare`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} lg={3}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Carga máxima (kg)"
                            size="small"
                            value={_.get(currentRecord, `transport_unit${unit}.max_load_weight`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}.max_load_weight`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} lg={3}>
                        <TextField
                            autoFocus
                            fullWidth
                            label={authUser?.current_company?.id === 80 ? "Volumen máximo (L)" : "Volumen máximo (M3)"}
                            Marca
                            size="small"
                            value={_.get(currentRecord, `transport_unit${unit}.max_load_volume`) || ''}
                            onChange={(e) => {
                                var newObj = _.set(currentRecord, `transport_unit${unit}.max_load_volume`, e.target.value);
                                setCurrentRecord({...newObj})
                            }}
                        />
                    </Grid>
                    
                </Grid>
            </Paper>
        )
    }

    // Que tiene de particular este vehiculo al rutear, no como rutea.
    //
    // Antes esta tarjeta era un solo check —evitar peajes— y las otras doce opciones vivian
    // detras de un boton "Ruteo" en el pie del modal, donde nadie las veia sin entrar. Ahora la
    // tarjeta lista lo que el vehiculo pisa y el engranaje abre el dialogo para cambiarlo.
    const renderOtherSettingsFields = () => {
        const filas = resumenParticular(currentRecord, editOptions?.centres)

        return (
            <Paper variant="outlined" sx={{mt:1, p: 2}}>
                <Div sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        { filas.length > 0
                            ? 'Lo que este vehículo hace distinto al rutear. El resto lo hereda de la empresa.'
                            : 'Este vehículo rutea con la configuración de la empresa: no tiene nada personalizado.' }
                    </Typography>
                    {/* El dialogo guarda contra el vehiculo, asi que necesita que exista. En uno
                        nuevo el engranaje queda apagado y el tooltip dice por que. */}
                    <Tooltip
                        arrow
                        title={currentRecord?.id
                            ? 'Configurar el ruteo de este vehículo'
                            : 'Guarda el vehículo para poder configurar su ruteo'}>
                        <span>
                            <IconButton
                                color="primary"
                                size="small"
                                disabled={!currentRecord?.id}
                                onClick={() => setRoutingOpen(true)}>
                                    <SettingsOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Div>

                { filas.length > 0 &&
                    <Grid container spacing={2} sx={{mt: 1}}>
                        { filas.map((fila) => (
                            <Grid item xs={12} sm={6} md={4} key={fila.key}>
                                <Typography variant="caption" color="text.secondary">{fila.label}</Typography>
                                <Typography variant="body2">{fila.valor}</Typography>
                            </Grid>
                        )) }
                    </Grid>
                }
            </Paper>
        )
    }

    const modalTitle = () => {
        if(currentRecord){
            return currentRecord.code
        } else {
            return loadingRecord ? 'Cargando' : 'Nuevo vehículo'
        }
    }

    return (
        <React.Fragment>
            {/* Mientras esta abierto el de ruteo, este se esconde en vez de quedar debajo: dos
                modales apilados no dejan claro cual manda. El formulario no se pierde, porque su
                estado vive en este componente y el que se cierra es solo el Dialog. */}
            <Dialog
                fullWidth={true}
                maxWidth={"md"}
                open={isOpen && !routingOpen}
                onClose={onClose}>
                <DialogTitle>
                    <Typography variant={"h2"} color="primary">{modalTitle}</Typography>
                    {currentRecord &&
                        <Chip
                            size="small"
                            color={currentRecord.active === false ? "default" : "success"}
                            label={currentRecord.active === false ? "Inactivo" : "Activo"}
                            sx={{mt: 1}}
                        />
                    }
                </DialogTitle>
                <DialogContent>
                    { loadingRecord ?
                        <Div sx={{ textAlign: 'center' }}>
                            <CircularProgress />
                        </Div>
                    :
                        <React.Fragment>
                            <Typography variant={"h4"} color="primary">Datos generales</Typography>
                            { renderTransportHeaderFields() }

                            <Typography variant={"h4"} color="primary">Unidades de transporte</Typography>
                            { renderTransportUnitFields(1) }
                            { currentRecord?.kind == 'composed' && renderTransportUnitFields(2) }

                            <Typography variant={"h4"} color="primary" sx={{mt: 3}}>Otras configuraciones</Typography>
                            { renderOtherSettingsFields() }

                            {/* { true ?
                                renderTransportUnitFields(2)
                                :
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    sx={{mt: 1}}
                                    startIcon={<ControlPointOutlinedIcon/>}
                                    onClick={() => setCurrentRecord({...currentRecord, transport_unit2: {}})}>
                                        Agregar unidad de transporte
                                </Button>
                            } */}
                        </React.Fragment>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { onClose() } }>Cerrar</Button>
                    <LoadingButton
                        loading={toggleActivationLoading}
                        variant="outlined"
                        color={currentRecord?.active === false ? "success" : "warning"}
                        size="small"
                        disabled={!currentRecord}
                        onClick={toggleActivation}>
                            {currentRecord?.active === false ? 'Activar vehículo' : 'Desactivar vehículo'}
                    </LoadingButton>
                    <LoadingButton
                        loading={saving}
                        variant="contained"
                        color="success"
                        size="small"
                        endIcon={<SaveOutlinedIcon/>}
                        loadingPosition="end"
                        onClick={saveRecord}>
                            { saving ? 'Guardando' : 'Guardar' }
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <RoutingSettingsDialog
                open={routingOpen}
                onClose={() => setRoutingOpen(false)}
                scope="vehicle"
                target={currentRecord}
                inherited={currentRecord?.company?.routing_settings}
                onSaved={refreshRoutingSettings}
            />
        </React.Fragment>
    )
}

export default VehicleEditModal;
